import {
  CommunicationStatus,
  failure,
  getCommunicationStatus,
  getCommunicationStatusDetailsOrUndefined,
  getCommunicationStatusOrUndefined,
  HttpOrCommunicationStatus,
  isClientFailure,
  isClientFailureNotAuthorized,
  isMissing,
  isNumber,
  isRetryableFailure,
  isString,
  JsonValue,
  networkFailure,
  success,
} from '@art-suite/art-core-ts';
import { z, zodLess, zodLessErrorMessage, ZodLessInfer, ZodLessSchema } from '@kaiizen/zod-less';

export class ErrorWithStatus extends Error {
  public status: number; // http status code e.g. 404, 400, 500, etc.
  public remoteMessage?: string; // CONTEXT: instead of `payload`, return a string message to the caller OR, in ZodLessApiClient, this will be set with the error's message
  public response?: any; // CONTEXT: the requests/s response object
  public request?: any; // CONTEXT: the request object
  public payload?: JsonValue; // OVERRIDE custom payload to return to the client IMPORTANT: this WILL BE SENT TO THE CALLER UPON THIS ERROR - DON'T INCLUDE ANY SENSITIVE DATA
  public communicationStatus: CommunicationStatus; // e.g. "success", "missing", "clientFailure", "serverFailure", etc. see https://github.com/art-suite/art-suite-ts/blob/main/packages/art-core-ts-communication-status

  constructor(message: string, status: HttpOrCommunicationStatus, options?: { response?: any; payload?: JsonValue; request?: any; remoteMessage?: string }) {
    super(message);
    const statusDetails = getCommunicationStatusDetailsOrUndefined(status);
    this.status = isNumber(status) ? status : statusDetails?.httpStatus ?? 0;
    this.response = options?.response;
    this.payload = options?.payload;
    this.request = options?.request;
    this.remoteMessage = options?.remoteMessage;
    this.communicationStatus = statusDetails?.communicationStatus ?? failure;
  }
}

/**
 * Gets the communication status from an error or just a communication status.
 *
 * Return success if no error is passed in. will return failure if a communication status can't be derived from the error.
 *
 * @param errorOrJustCommunicationStatus - The error or just a communication status (HTTP status code, CommunicationStatus, ErrorWithStatus, etc.)
 * @returns The CommunicationStatus string e.g. "success", "missing", "clientFailure", "serverFailure", etc. - see https://github.com/art-suite/art-suite-ts/tree/main/packages/art-core-ts-communication-status
 */
export const getErrorCommunicationStatus = (errorOrJustCommunicationStatus: any): CommunicationStatus => {
  if (errorOrJustCommunicationStatus == null) return success;

  const communicationStatus = getCommunicationStatusOrUndefined(errorOrJustCommunicationStatus)
    ?? getCommunicationStatusOrUndefined(errorOrJustCommunicationStatus.communicationStatus)
    ?? getCommunicationStatusOrUndefined(errorOrJustCommunicationStatus.status);
  if (communicationStatus) return communicationStatus;

  return failure;
};

/**
 * Gets the communication status from an error or just a communication status.
 *
 * Returns undefined if no communication status can be derived from the error.
 *
 * @param errorOrJustCommunicationStatus - The error or just a communication status (HTTP status code, CommunicationStatus, ErrorWithStatus, etc.)
 * @returns The CommunicationStatus string e.g. "success", "missing", "clientFailure", "serverFailure", etc. - see https://github.com/art-suite/art-suite-ts/tree/main/packages/art-core-ts-communication-status or undefined if no communication status is found
 */
export const getErrorCommunicationStatusOrUndefined = (errorOrJustCommunicationStatus: any): CommunicationStatus | undefined => {
  if (errorOrJustCommunicationStatus == null) return undefined;

  const communicationStatus = getCommunicationStatusOrUndefined(errorOrJustCommunicationStatus)
    ?? getCommunicationStatusOrUndefined(errorOrJustCommunicationStatus.communicationStatus)
    ?? getCommunicationStatusOrUndefined(errorOrJustCommunicationStatus.status);
  if (communicationStatus) return communicationStatus;

  return undefined;
};

export const createNetworkFailureError = (message: string = 'Network failure', response?: any, payload?: JsonValue) =>
  new ErrorWithStatus(message, networkFailure, { response, payload });

/**
 * Creates a 404 Not Found error for when a requested resource doesn't exist.
 *
 * This error should be thrown when:
 * - A specific record/entity cannot be found by ID
 * - A requested endpoint or resource doesn't exist
 * - A file or document cannot be located
 *
 * @param message - Human-readable error message (default: '404 Not found')
 * @param response - Optional response object from the failed operation
 * @param payload - Optional JSON payload containing additional error details
 * @returns ErrorWithStatus instance with 404 status code
 *
 * @example
 * // In API handler when user not found
 * const user = await findUserById(userId);
 * if (!user) {
 *   throw createNotFoundError(`User with ID ${userId} not found`);
 * }
 *
 * @example
 * // In model layer when record doesn't exist
 * const record = await db.record.findUnique({ where: { id } });
 * if (!record) {
 *   throw createNotFoundError('Record not found', null, { recordId: id });
 * }
 */
export const createNotFoundError = (message: string = '404 Not found', response?: any, payload?: JsonValue) =>
  new ErrorWithStatus(message, 404, { response, payload });

/**
 * Creates a 400 Bad Request error for invalid client input or malformed requests.
 *
 * This error should be thrown when:
 * - Request validation fails (missing required fields, invalid formats)
 * - Request body is malformed or contains invalid data
 * - Query parameters are invalid or out of range
 * - Business logic validation fails due to client input
 *
 * @param message - Human-readable error message (default: '400 Invalid request')
 * @param response - Optional response object from the failed operation
 * @param payload - Optional JSON payload containing validation details or field errors
 * @returns ErrorWithStatus instance with 400 status code
 *
 * @example
 * // In API handler when validation fails
 * if (!email || !isValidEmail(email)) {
 *   throw createInvalidRequestError('Valid email address is required');
 * }
 *
 * @example
 * // In model layer with detailed validation errors
 * const validationErrors = validateUserData(userData);
 * if (validationErrors.length > 0) {
 *   throw createInvalidRequestError('Invalid user data', null, { errors: validationErrors });
 * }
 */
export const createInvalidRequestError = (message: string = '400 Invalid request', response?: any, payload?: JsonValue) =>
  new ErrorWithStatus(message, 400, { response, payload });

/**
 * Creates a 403 Forbidden error for unauthenticated clients.
 *
 * ⚠️ DEPRECATED: Use createForbiddenError instead for most cases.
 * This function is temporarily deprecated to force migration to the correct error types.
 *
 * This error should ONLY be used when:
 * - The client is completely unauthenticated (no valid token/session)
 * - Authentication credentials are missing or invalid
 *
 * For authenticated users who lack permission, use createForbiddenError instead.
 *
 * @param message - Human-readable error message (default: '403 Not authorized / forbidden')
 * @param response - Optional response object from the failed operation
 * @param payload - Optional JSON payload containing additional error details
 * @returns ErrorWithStatus instance with 403 status code
 *
 * @example
 * // In API handler when no authentication token provided
 * if (!request.headers.authorization) {
 *   throw createNotAuthorizedError('Authentication required');
 * }
 *
 * @deprecated Use createForbiddenError for most authorization failures
 */
export const createNotAuthorizedError = (message: string = '403 Not authorized: forbidden', response?: any, payload?: JsonValue) =>
  new ErrorWithStatus(message, 403, { response, payload });

export const createNotAuthorizedError401 = (message: string = '401 Not authorized', response?: any, payload?: JsonValue) =>
  new ErrorWithStatus(message, 401, { response, payload });

/**
 * Creates a 403 Forbidden error for authenticated clients who lack permission.
 *
 * This error should be thrown when:
 * - The client is authenticated but doesn't have permission for the requested action
 * - User lacks role-based access to specific resources
 * - Resource ownership validation fails (user trying to access another user's data)
 * - Business rule restrictions prevent the action
 *
 * @param message - Human-readable error message (default: '403 Forbidden')
 * @param response - Optional response object from the failed operation
 * @param payload - Optional JSON payload containing permission details or required roles
 * @returns ErrorWithStatus instance with 403 status code
 *
 * @example
 * // In API handler when user lacks permission
 * if (!userHasPermission(user, 'delete', resource)) {
 *   throw createForbiddenError('You do not have permission to delete this resource');
 * }
 *
 * @example
 * // In model layer when ownership validation fails
 * if (record.userId !== currentUserId) {
 *   throw createForbiddenError('Access denied', null, {
 *     requiredOwner: record.userId,
 *     currentUser: currentUserId
 *   });
 * }
 */
export const createForbiddenError = (message: string = '403 Forbidden', response?: any, payload?: JsonValue) =>
  new ErrorWithStatus(message, 403, { response, payload });

/**
 * Creates a 500 Internal Server Error for unexpected server-side failures.
 *
 * This error should be thrown when:
 * - Database connection or query failures occur
 * - External service integrations fail unexpectedly
 * - Unhandled exceptions or programming errors occur
 * - System resources are unavailable (file system, memory, etc.)
 * - Third-party API calls fail with server errors
 *
 * @param message - Human-readable error message (default: '500 Internal server error')
 * @param response - Optional response object from the failed operation
 * @param payload - Optional JSON payload containing error details, stack traces, or debugging info
 * @returns ErrorWithStatus instance with 500 status code
 *
 * @example
 * // In API handler when database operation fails
 * try {
 *   await db.user.create({ data: userData });
 * } catch (error) {
 *   throw createInternalServerError('Failed to create user', error, {
 *     operation: 'createUser',
 *     userId: userData.id
 *   });
 * }
 *
 * @example
 * // In model layer when external service fails
 * try {
 *   await externalApi.sendNotification(notification);
 * } catch (error) {
 *   throw createInternalServerError('Notification service unavailable', error);
 * }
 */
export const createInternalServerError = (message: string = '500 Internal server error', response?: any, payload?: JsonValue) =>
  new ErrorWithStatus(message, 500, { response, payload });

/**
 * Checks if an error is a 404 Not Found error by examining its status code.
 *
 * This utility function is useful for:
 * - Error handling logic that needs to differentiate 404s from other errors
 * - Conditional error processing based on error type
 * - Logging and monitoring systems that track specific error types
 *
 * @param e - The error object to check (can be any type)
 * @returns true if the error has a 404 status code, false otherwise
 *
 * @example
 * // In error handling middleware
 * try {
 *   await someOperation();
 * } catch (error) {
 *   if (isNotFoundError(error)) {
 *     // Handle 404s differently - maybe return empty result instead of error
 *     return { data: null, found: false };
 *   }
 *   throw error; // Re-throw other errors
 * }
 *
 * @example
 * // In API response logic
 * const result = await fetchUserData(userId);
 * if (isNotFoundError(result)) {
 *   return res.status(404).json({ message: 'User not found' });
 * }
 */
export const isNotFoundError = (e: any): boolean => {
  const communicationStatus = getCommunicationStatus(e.communicationStatus);
  return isString(communicationStatus) && isMissing(communicationStatus);
};

export const validateInput = <T extends ZodLessSchema>(input: any, inputSchema: T): ZodLessInfer<T> => {
  const schema = zodLess(inputSchema) as z.ZodType;
  const result = schema.safeParse(input);
  if (!result.success) {
    const message = zodLessErrorMessage(schema, input);
    throw createInvalidRequestError(message);
  }
  return result.data as any;
};

export const errorIsClientFailure = (error: any): error is ErrorWithStatus => error instanceof ErrorWithStatus && isClientFailure(error.communicationStatus);

export const errorIsClientFailureNotAuthorized = (error: any): error is ErrorWithStatus =>
  error instanceof ErrorWithStatus && isClientFailureNotAuthorized(error.communicationStatus);

export const errorIsRetryableFailure = (error: any): error is ErrorWithStatus =>
  error instanceof ErrorWithStatus && isRetryableFailure(error.communicationStatus);

export const errorIsMissing = (error: any): error is ErrorWithStatus => error instanceof ErrorWithStatus && isMissing(error.communicationStatus);
